import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface AccessDeviceCommandContext {
  core_device_id: string;
  revision: number;
  command_property_ids: string[];
  requires_property_selection: boolean;
}

const contextKeys = [
  'core_device_id',
  'revision',
  'command_property_ids',
  'requires_property_selection',
] as const;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const canonicalUuid = (value: unknown): value is string =>
  typeof value === 'string' && isUuid(value) && value === value.toLowerCase();
const canonicalUuidArray = (value: unknown): value is string[] => {
  if (!Array.isArray(value)) return false;

  for (let index = 0; index < value.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value, index)) return false;
    const propertyId: unknown = value[index];
    if (!canonicalUuid(propertyId)) return false;
    const previous: unknown = value[index - 1];
    if (previous !== undefined && (!canonicalUuid(previous) || previous >= propertyId))
      return false;
  }

  return true;
};

function invalid() {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '장비 작업 범위를 확인하지 못했습니다. 다시 시도해 주세요.',
  });
}

function parseContext(
  value: unknown,
  coreDeviceId: string,
  propertyId: string | undefined,
): AccessDeviceCommandContext {
  if (
    !record(value) ||
    ![contextKeys.length, contextKeys.length + 3].includes(Object.keys(value).length) ||
    !contextKeys.every((key) => key in value) ||
    Object.keys(value).some(
      (key) =>
        ![...contextKeys, 'purpose', 'last_change_actor_id', 'last_change_reason'].includes(key),
    ) ||
    (Object.keys(value).length > contextKeys.length &&
      (!['UNKNOWN', 'COMMON_ENTRANCE', 'ROOM_LOCK'].includes(String(value.purpose)) ||
        !(value.last_change_actor_id === null || canonicalUuid(value.last_change_actor_id)) ||
        !(value.last_change_reason === null || typeof value.last_change_reason === 'string'))) ||
    !canonicalUuid(value.core_device_id) ||
    value.core_device_id !== coreDeviceId ||
    typeof value.revision !== 'number' ||
    !Number.isSafeInteger(value.revision) ||
    value.revision <= 0 ||
    !canonicalUuidArray(value.command_property_ids) ||
    typeof value.requires_property_selection !== 'boolean'
  )
    throw invalid();

  const propertyIds = value.command_property_ids;
  if (
    (value.requires_property_selection && propertyIds.length > 0) ||
    (propertyId !== undefined &&
      (value.requires_property_selection ||
        propertyIds.length !== 1 ||
        propertyIds[0] !== propertyId))
  )
    throw invalid();

  return {
    core_device_id: coreDeviceId,
    revision: value.revision,
    command_property_ids: propertyIds,
    requires_property_selection: value.requires_property_selection,
  };
}

/** Read a frozen command context without coupling it to the root registry DTO. */
export async function readAccessDeviceCommandContext(
  client: ServiceClient,
  coreDeviceId: string,
  propertyId?: string,
  signal?: AbortSignal,
): Promise<AccessDeviceCommandContext> {
  if (!canonicalUuid(coreDeviceId) || (propertyId !== undefined && !canonicalUuid(propertyId)))
    throw invalid();

  const value = await client.get<unknown>(`admin/access-devices/${coreDeviceId}/command-context`, {
    params: { property_id: propertyId },
    signal,
    headers: { 'Cache-Control': 'no-store' },
  });
  return parseContext(value, coreDeviceId, propertyId);
}
